import {
  GeneralResourceDetail,
  MpxItemResponse,
  ResourceDetailsUpdate,
  TaskDataResponse,
  TraitId,
} from '@cibo/core'
import { FieldNameCell } from '@cibo/landmanager'
import { ResourceDetailFeatureTaskEditorProps } from '@cibo/ui'
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { UseQueryResult } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { TaskDataModel } from '../../../ProgramEngagementPlayer/models/TaskDataModel'
import { MultiselectChangeAllInput } from './MultiselectChangeAllInput'
import { MultiselectCheckboxProvider } from './MultiselectCheckboxProvider'
import { useMultiselectCheckboxContext } from './MultiselectCheckboxContext'
import { MultiselectFieldInput } from './MultiselectFieldInput'

interface FieldMultiSelectProps {
  sources: TraitId[] | string[]
  optionsList?: { value: string; label: string }[]
  traitId: TraitId
  handleChange: (
    updates: ResourceDetailsUpdate[]
  ) => Promise<void | MpxItemResponse<GeneralResourceDetail>>
  dataEntryFieldDetails?: UseQueryResult<TaskDataModel, Error>
  disabled?: boolean
  verifyExtraRequirements?: boolean
}

const MultiselectCheckboxFieldTableContent = () => {
  const { t } = useTranslation('@cibo/programs/MultiselectCheckboxFieldTable')
  const {
    fieldAnswers,
    allDetailsAreCibo,
    options,
    saveCiboValues,
    saveAllCiboValues,
    programEngagementFields,
  } = useMultiselectCheckboxContext()

  return (
    <TableContainer style={{ borderWidth: 0 }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t('field', { ns: '@cibo/landmanager/SelectRollupEditor' })}</TableCell>
            <TableCell>{t('tableHeader')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {programEngagementFields.data?.items && programEngagementFields.data.items.length > 1 && (
            <TableRow key="all">
              <TableCell>
                <Box sx={{ marginLeft: 2, py: 2 }}>
                  <Typography sx={{ fontWeight: 'bold', my: 2 }}>
                    {t('answerForAllFields', { ns: '@cibo/landmanager/SelectRollupEditor' })}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <MultiselectChangeAllInput
                  style={{
                    marginTop: allDetailsAreCibo ? '29px' : '31px',
                    // to retain centering when there's not a button below
                    marginBottom: allDetailsAreCibo ? undefined : '31px',
                  }}
                />
                {allDetailsAreCibo && (
                  <Box sx={{ marginLeft: 1, marginTop: '-5px' }}>
                    <Button variant="text" size="small" onClick={saveAllCiboValues}>
                      {t('useCiboValues')}
                    </Button>
                  </Box>
                )}
              </TableCell>
            </TableRow>
          )}
          {programEngagementFields.data?.items.map(field => (
            <TableRow key={field.resourceId}>
              <TableCell>
                <Box sx={{ marginLeft: 2, py: 2 }}>
                  {/* @ts-ignore this isn't a data grid, so it's missing some props */}
                  <FieldNameCell row={field} />
                </Box>
              </TableCell>
              <TableCell>
                <Stack direction="column">
                  <MultiselectFieldInput
                    disabled={options.length === 0 || fieldAnswers[field.resourceId]?.immutable}
                    style={{
                      // to retain centering when there's a button below
                      marginTop: fieldAnswers[field.resourceId]?.source === 'cibo' ? '30px' : null,
                    }}
                    row={field}
                    value={fieldAnswers[field.resourceId] || []}
                    options={options}
                  />
                  {fieldAnswers[field.resourceId]?.source === 'cibo' && (
                    <Box sx={{ marginLeft: 1, marginTop: '-5px' }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => saveCiboValues(field.resourceId)}
                      >
                        {t('useCiboValues')}
                      </Button>
                    </Box>
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export const MultiselectCheckboxFieldTable = ({
  detailRequirements,
  engagementId,
  onError,
  dataEntryFieldDetails,
  onSuccess,
  onUpdating,
  sources,
  optionsList,
  traitId,
  handleChange,
  verifyExtraRequirements,
}: Omit<
  ResourceDetailFeatureTaskEditorProps,
  'ownerName' | 'userRole' | 'resourceIds' | 'engagement'
> &
  FieldMultiSelectProps) => {
  const year = detailRequirements?.[0]?.year

  return (
    <MultiselectCheckboxProvider
      engagementId={engagementId}
      sources={sources}
      optionsList={optionsList}
      traitId={traitId}
      handleChange={handleChange}
      onError={onError}
      onSuccess={onSuccess}
      onUpdating={onUpdating}
      dataEntryFieldDetails={dataEntryFieldDetails as UseQueryResult<TaskDataResponse, Error>}
      year={year}
      verifyExtraRequirements={verifyExtraRequirements ?? true}
    >
      <MultiselectCheckboxFieldTableContent />
    </MultiselectCheckboxProvider>
  )
}
